/*************************************************************
 * PredictiveInsightsTab.js - Predictive insights tab
 *************************************************************/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';

export const PredictiveInsightsTab = () => {
    const { state, setLoading, setError, addNotification } = useAppContext();
    const [predictions, setPredictions] = useState(null);

    const loadPredictions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await api.getPredictiveInsights(state.filters);
            setPredictions(data);

            addNotification({
                type: 'success',
                message: 'Predictive analysis completed',
                duration: 3000
            });
        } catch (error) {
            console.error('[Predictions] Error loading:', error);
            setError(error);
            addNotification({
                type: 'danger',
                message: 'Failed to generate predictions: ' + error.message
            });
        } finally {
            setLoading(false);
        }
    }, [state.filters, setLoading, setError, addNotification]);

    useEffect(() => {
        loadPredictions();
    }, [loadPredictions]);

    const ForecastChart = ({ forecastData }) => {
        const chartRef = useRef(null);
        const chartInstance = useRef(null);

        useEffect(() => {
            if (!forecastData?.predictions || !chartRef.current) return;
            if (!window.Chart) return;

            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            chartInstance.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: forecastData.predictions.map(p => `Day ${p.day}`),
                    datasets: [{
                        label: 'Predicted Defect Rate (%)',
                        data: forecastData.predictions.map(p => p.predicted_defect_rate),
                        borderColor: '#17a2b8',
                        backgroundColor: 'rgba(23, 162, 184, 0.1)',
                        borderDash: [5, 5],
                        tension: 0.4,
                        fill: true
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'Defect Rate (%)'
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            display: false
                        }
                    }
                }
            });

            return () => {
                if (chartInstance.current) {
                    chartInstance.current.destroy();
                }
            };
        }, [forecastData]);

        return <canvas ref={chartRef} style={{ height: '200px' }} />;
    };

    if (state.loading) {
        return (
            <LoadingSpinner
                message="Generating predictions..."
                size="large"
            />
        );
    }

    return (
        <ErrorBoundary error={state.error} onRetry={loadPredictions}>
            <div className="predictive-content fade-in">
                {predictions && (
                    <div>
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="card shadow-sm">
                                    <div className="card-header bg-info text-white">
                                        <h5 className="mb-0">
                                            <i className="fas fa-chart-line mr-2"></i>
                                            Defect Rate Forecast
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {predictions.defect_rate_forecast ? (
                                            <div>
                                                <div className={`alert alert-${
                                                    predictions.defect_rate_forecast.current_trend === 'increasing' ? 'warning' :
                                                    predictions.defect_rate_forecast.current_trend === 'decreasing' ? 'success' : 'info'
                                                }`}>
                                                    <i className={`fas fa-arrow-${
                                                        predictions.defect_rate_forecast.current_trend === 'increasing' ? 'up' :
                                                        predictions.defect_rate_forecast.current_trend === 'decreasing' ? 'down' : 'right'
                                                    } mr-2`}></i>
                                                    Trend: {predictions.defect_rate_forecast.current_trend}
                                                    <small className="d-block mt-1">
                                                        {predictions.defect_rate_forecast.recommendation}
                                                    </small>
                                                </div>
                                                <ForecastChart forecastData={predictions.defect_rate_forecast} />
                                            </div>
                                        ) : (
                                            <p className="text-muted">No forecast data available</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card shadow-sm">
                                    <div className="card-header bg-danger text-white">
                                        <h5 className="mb-0">
                                            <i className="fas fa-exclamation-triangle mr-2"></i>
                                            Failure Risk Assessment
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {predictions.failure_risk_assessment ? (
                                            <div>
                                                <div className="text-center mb-4">
                                                    <h1 className={`text-${
                                                        predictions.failure_risk_assessment.risk_level === 'high' ? 'danger' :
                                                        predictions.failure_risk_assessment.risk_level === 'medium' ? 'warning' : 'success'
                                                    }`}>
                                                        {predictions.failure_risk_assessment.overall_risk_score}%
                                                    </h1>
                                                    <p className="text-muted">Overall Risk Score</p>
                                                </div>
                                                <h6>Risk Factors:</h6>
                                                <ul className="mb-3">
                                                    {predictions.failure_risk_assessment.risk_factors?.map((factor, idx) => (
                                                        <li key={idx}>{factor}</li>
                                                    ))}
                                                </ul>
                                                <h6>Mitigation Actions:</h6>
                                                <ul className="mb-0">
                                                    {predictions.failure_risk_assessment.mitigation_actions?.map((action, idx) => (
                                                        <li key={idx}>{action}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : (
                                            <p className="text-muted">No risk assessment available</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card shadow-sm">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <i className="fas fa-tools text-primary mr-2"></i>
                                            Maintenance Predictions
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {predictions.maintenance_recommendations?.station_predictions ? (
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Station</th>
                                                            <th>Defect Rate</th>
                                                            <th>Urgency</th>
                                                            <th>Recommended Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {predictions.maintenance_recommendations.station_predictions.map((station, idx) => (
                                                            <tr key={idx}>
                                                                <td>{station.station_id}</td>
                                                                <td>{station.defect_rate.toFixed(1)}%</td>
                                                                <td>
                                                                    <span className={`badge badge-${
                                                                        station.maintenance_urgency === 'high' ? 'danger' :
                                                                        station.maintenance_urgency === 'medium' ? 'warning' : 'success'
                                                                    }`}>
                                                                        {station.maintenance_urgency}
                                                                    </span>
                                                                </td>
                                                                <td>{station.recommended_action}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0">
                                                No maintenance predictions available
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {predictions.anomaly_detection && predictions.anomaly_detection.anomalies_detected > 0 && (
                            <div className="row">
                                <div className="col-12">
                                    <div className="card shadow-sm border-warning">
                                        <div className="card-header bg-warning">
                                            <h5 className="mb-0">
                                                <i className="fas fa-bell mr-2"></i>
                                                Anomalies Detected
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <p>
                                                {predictions.anomaly_detection.anomalies_detected} anomalies detected
                                            </p>
                                            <ul>
                                                {predictions.anomaly_detection.anomalies.map((anomaly, idx) => (
                                                    <li key={idx}>
                                                        {anomaly.type.replace(/_/g, ' ')} at Station {anomaly.station} 
                                                        (Hour: {anomaly.hour}:00)
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};